import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable} from 'rxjs';
import {
    CommissionRuleCriteria,
    CommissionRuleConditionOperator,
    CommissionRulesResponse,
    SearchCommissionRulesParams,
    CommissionRuleDetail,
    CommissionRuleUiComponent,
    CommissionRuleActionType,
    CommissionEvaluationsResponse,
    EvaluateResponse,
    ApplyFixesResponse
} from '../../shared/model/commission-rules-result';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';

@Injectable({
    providedIn: 'root'
})
export class CommissionRulesService {

    private readonly commissionCategoryId = 3;

    constructor(private httpClient: HttpClient) {}

    getRules({page, sort}: SearchCommissionRulesParams): Observable<CommissionRulesResponse> {
        const params: Record<string, string | number> = {
            pageSize: page?.pageSize || 25,
            pageNumber: (page?.pageIndex || 0) + 1,
            propertyName: sort?.active || 'id',
            orderKey: sort?.direction || 'desc',
            categoryId: this.commissionCategoryId,
        };

        removeEmptyParams(params);

        return this.httpClient.get<CommissionRulesResponse>('dbov2/workflow', {params: {...params}})
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    getRuleDetails(workflowId: string): Observable<CommissionRuleDetail> {
        return this.httpClient.get<CommissionRuleDetail>(`dbov2/workflow/${workflowId}`)
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    createRule(request: any): Observable<any> {
        return this.httpClient.post('dbov2/workflow', request)
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    updateRule(request: any): Observable<any> {
        return this.httpClient.put('dbov2/workflow', request)
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    deleteRule(workflowId: number): Observable<void> {
        return this.httpClient.delete<void>('dbov2/workflow', {params: {workflowId}})
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    getCriteria(): Observable<CommissionRuleCriteria[]> {
        return this.httpClient.get<CommissionRuleCriteria[]>('dbov2/workflow-target-criteria', {
            params: {categoryId: this.commissionCategoryId}
        }).pipe(catchError((error: HttpErrorResponse) => {
            throw new Error(error?.message || 'Server error');
        }));
    }

    getOperators(): Observable<CommissionRuleConditionOperator[]> {
        return this.httpClient.get<CommissionRuleConditionOperator[]>('dbov2/workflow-logic-operator')
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error?.message || 'Server error');
            }));
    }

    getUiComponents(criteriaId: string): Observable<CommissionRuleUiComponent[]> {
        return this.httpClient.get<CommissionRuleUiComponent[]>('dbov2/alert-rule/elements-ui', {
            params: {criteriaId}
        }).pipe(catchError((error: HttpErrorResponse) => {
            throw new Error(error?.message || 'Server error');
        }));
    }

    getActionTypes(): Observable<CommissionRuleActionType[]> {
        return this.httpClient.get<CommissionRuleActionType[]>('dbov2/workflow-rule-action-type')
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error?.message || 'Server error');
            }));
    }

    getEvaluations(params: Record<string, string | number>): Observable<CommissionEvaluationsResponse> {
        return this.httpClient.get<CommissionEvaluationsResponse>('dbov2/commission-rule/evaluations', {params})
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    runEvaluation(workflowId?: number): Observable<EvaluateResponse> {
        const url = workflowId
            ? `dbov2/commission-rule/evaluate/${workflowId}`
            : 'dbov2/commission-rule/evaluate';
        return this.httpClient.post<EvaluateResponse>(url, {})
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    runFixes(workflowId?: number): Observable<ApplyFixesResponse> {
        const url = workflowId
            ? `dbov2/commission-rule/apply-fixes/${workflowId}`
            : 'dbov2/commission-rule/apply-fixes';
        return this.httpClient.post<ApplyFixesResponse>(url, {})
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    forceApplyFixes(workflowId?: number): Observable<ApplyFixesResponse> {
        const url = workflowId
            ? `dbov2/commission-rule/force-apply-fixes/${workflowId}`
            : 'dbov2/commission-rule/force-apply-fixes';
        return this.httpClient.post<ApplyFixesResponse>(url, {})
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }

    updateEvaluationStatus(evaluationId: number, status: string, reason?: string): Observable<void> {
        return this.httpClient.post<void>(`dbov2/commission-rule/evaluations/${evaluationId}/status`, {status, reason})
            .pipe(catchError((error: HttpErrorResponse) => {
                throw new Error(error.message || 'Server error');
            }));
    }
}
